/**
 * ManualBillDialog — type a vendor bill by hand, no OCR, no file upload.
 *
 * Sprint 2026-08 · Owner ask — some purchases have no paper trail
 * (petty cash, verbal deals, torn kaccha bills the OCR can't parse).
 * This dialog produces a bill that is indistinguishable downstream from
 * a scanned bill: same schema, same dedup, same stock ledger writes,
 * same vendor rollups.
 *
 * Design goals:
 *   • Feel like a lightweight expense entry, not a heavy invoice form.
 *   • Only vendor + at least one item (or a total) are required.
 *   • GST auto-computes as user types — cashier can see the tax number
 *     matches the paper bill before saving.
 *   • Backend accepts either fully-formed totals or lets us omit them
 *     and it'll compute from lines.
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, FileEdit } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, fmtMoney } from '../lib/api';

const GST_RATES = [0, 5, 12, 18, 28];

const blankRow = () => ({
    id: crypto.randomUUID?.() || String(Math.random()),
    name: '',
    quantity: '',
    unit_price: '',
    tax_rate: '',
    hsn: '',
});

/**
 * Compute a line item's totals given raw string inputs. Returns numbers
 * (0 if blank) — makes rendering totals trivial and never NaN.
 */
function computeLine(row) {
    const qty = Number(row.quantity) || 0;
    const rate = Number(row.unit_price) || 0;
    const tax = Number(row.tax_rate) || 0;
    const base = qty * rate;
    const taxAmt = base * (tax / 100);
    return { base, taxAmt, total: base + taxAmt };
}


export function ManualBillDialog({ open, onOpenChange, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const [vendor, setVendor] = useState('');
    const [billDate, setBillDate] = useState(today);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [rows, setRows] = useState([blankRow()]);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const totals = useMemo(() => {
        let subtotal = 0;
        let tax = 0;
        for (const r of rows) {
            const { base, taxAmt } = computeLine(r);
            subtotal += base;
            tax += taxAmt;
        }
        return {
            subtotal: Number(subtotal.toFixed(2)),
            tax: Number(tax.toFixed(2)),
            total: Number((subtotal + tax).toFixed(2)),
        };
    }, [rows]);

    function reset() {
        setVendor('');
        setBillDate(today);
        setInvoiceNumber('');
        setRows([blankRow()]);
        setNotes('');
    }

    function addRow() {
        setRows((p) => [...p, blankRow()]);
    }
    function removeRow(id) {
        setRows((p) => (p.length <= 1 ? p : p.filter((r) => r.id !== id)));
    }
    function updateRow(id, patch) {
        setRows((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    }

    async function submit(force = false) {
        const v = vendor.trim();
        if (!v) {
            toast.error('Vendor name is required.');
            return;
        }
        // Filter blank rows silently
        const validRows = rows.filter(
            (r) => r.name.trim() && Number(r.quantity) > 0 && Number(r.unit_price) >= 0,
        );
        if (validRows.length === 0) {
            toast.error('Add at least one line item (name + quantity + rate).');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                vendor: v,
                bill_date: billDate,
                invoice_number: invoiceNumber.trim() || undefined,
                items: validRows.map((r) => ({
                    name: r.name.trim(),
                    quantity: Number(r.quantity),
                    unit_price: Number(r.unit_price),
                    tax_rate: r.tax_rate === '' ? undefined : Number(r.tax_rate),
                    hsn: r.hsn.trim() || undefined,
                    total: Number(computeLine(r).total.toFixed(2)),
                })),
                subtotal: totals.subtotal,
                tax: totals.tax,
                total: totals.total,
                notes: notes.trim() || undefined,
                force,
            };
            const { status, data } = await api.createManualBill(payload);
            if (status === 409 || data?.duplicate) {
                const confirm = window.confirm(
                    'This looks like a duplicate of an existing bill (same vendor, date, invoice #, total).\n\nSave it anyway?',
                );
                if (confirm) {
                    setSaving(false);
                    return submit(true);
                }
                setSaving(false);
                return;
            }
            if (data?.rejected) {
                toast.error(data.message || 'Bill was rejected.');
                setSaving(false);
                return;
            }
            toast.success(`Bill saved: ${data.bill?.vendor || v} · ${fmtMoney(data.bill?.total || totals.total)}`);
            reset();
            onOpenChange(false);
            onCreated?.(data.bill);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save the bill.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent
                className="max-w-3xl max-h-[92vh] overflow-y-auto"
                data-testid="manual-bill-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileEdit size={18} className="text-primary" />
                        Type a bill manually
                    </DialogTitle>
                    <DialogDescription>
                        For purchases with no paper bill (petty cash, verbal deals, kaccha bills).
                        The bill goes into your stock ledger and vendor reports just like a scanned one.
                    </DialogDescription>
                </DialogHeader>

                {/* ── Header row ─────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-3 md:col-span-1">
                        <Label className="text-xs mb-1.5 block">Vendor *</Label>
                        <Input
                            value={vendor}
                            onChange={(e) => setVendor(e.target.value)}
                            placeholder="e.g. Milkman, Tea Stall, Local Kirana"
                            data-testid="manual-bill-vendor"
                        />
                    </div>
                    <div>
                        <Label className="text-xs mb-1.5 block">Bill date</Label>
                        <Input
                            type="date"
                            value={billDate}
                            onChange={(e) => setBillDate(e.target.value)}
                            data-testid="manual-bill-date"
                        />
                    </div>
                    <div>
                        <Label className="text-xs mb-1.5 block">Invoice # (optional)</Label>
                        <Input
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            placeholder="e.g. INV-042"
                            data-testid="manual-bill-invoice"
                        />
                    </div>
                </div>

                {/* ── Line items ────────────────────────────────────── */}
                <div className="mt-2">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs">Line items *</Label>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={addRow}
                            className="gap-1 h-7"
                            data-testid="manual-bill-add-row"
                        >
                            <Plus size={11} /> Add row
                        </Button>
                    </div>

                    {/* Header row for md+ screens */}
                    <div className="hidden md:grid md:grid-cols-12 gap-2 px-1 text-[10px] uppercase text-muted-foreground tracking-wide mb-1">
                        <div className="col-span-5">Item</div>
                        <div className="col-span-2 text-right">Qty</div>
                        <div className="col-span-2 text-right">Rate ₹</div>
                        <div className="col-span-2 text-right">GST %</div>
                        <div className="col-span-1"></div>
                    </div>

                    <div className="space-y-2">
                        {rows.map((r, idx) => {
                            const { total } = computeLine(r);
                            return (
                                <div
                                    key={r.id}
                                    className="grid grid-cols-12 gap-2 items-center p-2 border rounded-md"
                                    data-testid={`manual-bill-row-${idx}`}
                                >
                                    <div className="col-span-12 md:col-span-5">
                                        <Input
                                            value={r.name}
                                            onChange={(e) => updateRow(r.id, { name: e.target.value })}
                                            placeholder="Item name (e.g. Milk 1L)"
                                            className="h-8 text-sm"
                                            data-testid={`manual-bill-row-${idx}-name`}
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-2">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={r.quantity}
                                            onChange={(e) => updateRow(r.id, { quantity: e.target.value })}
                                            placeholder="Qty"
                                            className="h-8 font-mono text-sm text-right"
                                            data-testid={`manual-bill-row-${idx}-qty`}
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-2">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={r.unit_price}
                                            onChange={(e) => updateRow(r.id, { unit_price: e.target.value })}
                                            placeholder="Rate"
                                            className="h-8 font-mono text-sm text-right"
                                            data-testid={`manual-bill-row-${idx}-rate`}
                                        />
                                    </div>
                                    <div className="col-span-3 md:col-span-2">
                                        <Select
                                            value={r.tax_rate === '' ? 'NONE' : String(r.tax_rate)}
                                            onValueChange={(v) => updateRow(r.id, { tax_rate: v === 'NONE' ? '' : Number(v) })}
                                        >
                                            <SelectTrigger className="h-8 text-sm" data-testid={`manual-bill-row-${idx}-gst`}>
                                                <SelectValue placeholder="GST" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="NONE">—</SelectItem>
                                                {GST_RATES.map((rt) => (
                                                    <SelectItem key={rt} value={String(rt)}>{rt}%</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        {rows.length > 1 && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => removeRow(r.id)}
                                                className="h-8 w-8 p-0"
                                                data-testid={`manual-bill-row-${idx}-remove`}
                                                title="Remove row"
                                            >
                                                <Trash2 size={13} />
                                            </Button>
                                        )}
                                    </div>
                                    {/* Line total — full width on mobile, hidden on desktop (rolled up in footer) */}
                                    <div className="col-span-12 md:hidden text-right text-xs text-muted-foreground font-mono">
                                        Line total: {fmtMoney(total)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Totals ────────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-2 mt-2 p-3 border rounded-md bg-muted/30 text-sm">
                    <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Subtotal</div>
                        <div className="font-mono tabular-nums">{fmtMoney(totals.subtotal)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase text-muted-foreground">GST</div>
                        <div className="font-mono tabular-nums">{fmtMoney(totals.tax)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Total</div>
                        <div
                            className="font-mono tabular-nums font-semibold text-primary"
                            data-testid="manual-bill-total"
                        >
                            {fmtMoney(totals.total)}
                        </div>
                    </div>
                </div>

                {/* ── Notes ─────────────────────────────────────────── */}
                <div>
                    <Label className="text-xs mb-1.5 block">Notes (optional)</Label>
                    <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Paid in cash, no bill given"
                        className="text-sm"
                        rows={2}
                        data-testid="manual-bill-notes"
                    />
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => { reset(); onOpenChange(false); }}
                        disabled={saving}
                        data-testid="manual-bill-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => submit(false)}
                        disabled={saving || !vendor.trim()}
                        className="gap-1"
                        data-testid="manual-bill-save"
                    >
                        <Save size={14} /> {saving ? 'Saving…' : 'Save bill'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ManualBillDialog;
